let socket = null;
let queued = [];
let reconnectTimer = null;
let connectionVersion = 0;
let activeConfig = null;
let heartbeatTimer = null;
let lastActivityAt = 0;

const clearHeartbeat = () => {
  clearInterval(heartbeatTimer);
  heartbeatTimer = null;
};

const startHeartbeat = (currentSocket, version) => {
  clearHeartbeat();
  lastActivityAt = Date.now();
  heartbeatTimer = setInterval(() => {
    if (version !== connectionVersion || currentSocket.readyState !== WebSocket.OPEN) return;
    if (Date.now() - lastActivityAt > 30000) {
      currentSocket.close(4000, 'heartbeat timeout');
      return;
    }
    currentSocket.send(JSON.stringify({ type: 'PING', data: { at: Date.now() } }));
  }, 10000);
};

const openSocket = version => {
  if (!activeConfig || version !== connectionVersion) return;
  const { pin, onMessageCallback } = activeConfig;
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  let player = null;
  try { player = JSON.parse(localStorage.getItem('h150_player') || sessionStorage.getItem('h150_player') || 'null'); } catch {}
  const protocols = player?.playerToken ? ['heart150', `player.${player.playerToken}`] : ['heart150'];
  const currentSocket = new WebSocket(
    `${protocol}//${location.host}/api/realtime/${encodeURIComponent(pin)}`,
    protocols
  );
  socket = currentSocket;

  currentSocket.addEventListener('open', () => {
    if (version !== connectionVersion) return currentSocket.close();
    activeConfig.retryCount = 0;
    startHeartbeat(currentSocket, version);
    onMessageCallback?.('CONNECTION_RESTORED', {});
    for (const message of queued) currentSocket.send(message);
    queued = [];
  });

  currentSocket.addEventListener('message', event => {
    if (version !== connectionVersion) return;
    lastActivityAt = Date.now();
    try {
      const { type, data } = JSON.parse(event.data);
      onMessageCallback?.(type, data);
    } catch (error) {
      console.error('[CloudflareRealtime] Invalid message', error);
    }
  });

  currentSocket.addEventListener('close', () => {
    clearHeartbeat();
    if (!activeConfig || version !== connectionVersion) return;
    const retry = activeConfig.retryCount++;
    const delay = Math.min(1000 * (2 ** retry), 10000);
    clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => openSocket(version), delay);
  });
};

export const realtimeSim = {
  connect(pin, onMessageCallback) {
    this.disconnect();
    connectionVersion += 1;
    activeConfig = { pin, onMessageCallback, retryCount: 0 };
    openSocket(connectionVersion);
    const resume = () => {
      if (!activeConfig || document.hidden || !navigator.onLine) return;
      if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
        clearTimeout(reconnectTimer);
        openSocket(connectionVersion);
      } else if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'PING', data: { at: Date.now() } }));
      }
    };
    activeConfig.resume = resume;
    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    return socket;
  },

  publish(type, payload = {}) {
    const message = JSON.stringify({ type, data: payload });
    if (socket?.readyState === WebSocket.OPEN) socket.send(message);
    else queued.push(message);
  },

  disconnect() {
    if (activeConfig?.resume) {
      window.removeEventListener('online', activeConfig.resume);
      document.removeEventListener('visibilitychange', activeConfig.resume);
    }
    connectionVersion += 1;
    activeConfig = null;
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
    clearHeartbeat();
    if (socket) socket.close(1000, 'navigation');
    socket = null;
    queued = [];
  }
};
